import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  message,
  DatePicker,
  Spin,
  Typography,
  Progress,
  Avatar,
  Badge,
  Divider,
  Tooltip,
  Select,
} from "antd";
import {
  FileTextOutlined,
  BlockOutlined,
  MessageOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CalendarOutlined,
  HistoryOutlined,
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CommentOutlined,
  FileSearchOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  SettingOutlined,
  CompassOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
import { dashboardService } from "../../services/DashboardService";
import LoadingSpinner from "../Common/LoadingSpinner";
import PageHeader from "../Common/PageHeader";
import StandardTable from "../Common/StandardTable";
import styles from "./Dashboard.module.scss";
import {
  CHART_COLORS,
  CHART_GRADIENTS,
  VIEW_MODES,
  SAMPLE_ACTIVITIES,
} from "./constants";
import type {
  DashboardStats,
  MonthlyStats,
  ViewMode,
  ActivityItem,
} from "./types";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Helper function to get activity icons
const getActivityIcon = (iconType: string, color: string) => {
  // Use CSS classes based on icon type
  const getIconClass = (type: string) => {
    switch (type) {
      case "file":
        return styles.iconFile;
      case "message":
        return styles.iconMessage;
      case "comment":
        return styles.iconComment;
      case "setting":
        return styles.iconSetting;
      default:
        return styles.iconFile;
    }
  };

  const iconClass = getIconClass(iconType);
  switch (iconType) {
    case "file":
      return <FileTextOutlined className={iconClass} />;
    case "message":
      return <MessageOutlined className={iconClass} />;
    case "comment":
      return <CommentOutlined className={iconClass} />;
    case "setting":
      return <SettingOutlined className={iconClass} />;
    default:
      return <FileTextOutlined className={iconClass} />;
  }
};

// Chart configuration
const CHART_CONFIG = {
  tooltip: {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  axis: {
    tick: { fontSize: 12, fill: "#64748b" },
    axisLine: { stroke: "#e2e8f0" },
  },
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const { t } = useTranslation();

  useEffect(() => {
    loadDashboardData();
  }, [viewMode]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      let data;

      // Load data based on view mode
      if (viewMode === "daily") {
        // Load last 7 days of data
        data = await dashboardService.getStats(7);
      } else if (viewMode === "weekly") {
        // Load last 4 weeks (28 days)
        data = await dashboardService.getStats(28);
      } else {
        // Load last 12 months
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 12);

        data = await dashboardService.getStats(
          undefined,
          startDate.toISOString().split("T")[0],
          endDate.toISOString().split("T")[0]
        );
      }

      setStats(data);

      // Process data based on view mode (with validation)
      const dailyStats =
        (data as any).trend_data || (data as any).daily_stats || [];
      console.log("API Response data:", data);
      console.log("Raw daily stats:", dailyStats);
      const processedData = processData(dailyStats, viewMode, data);
      console.log("Processed chart data:", processedData);
      setMonthlyData(processedData);
    } catch (error: any) {
      message.error(error.message || t("dashboard.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  // Process daily stats based on view mode
  const processData = (
    dailyStats: Array<{
      date: string;
      requests: number;
      conversations?: number;
      users?: number;
    }>,
    mode: ViewMode,
    currentStats?: DashboardStats
  ): MonthlyStats[] => {
    // Validate input - ensure dailyStats is an array
    if (!Array.isArray(dailyStats) || dailyStats.length === 0) {
      console.warn(
        "dailyStats is empty or not an array, returning sample data for testing"
      );
      // Return sample data if no real data available
      const sampleData = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return {
          month: date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          requests: Math.floor(Math.random() * 50) + 10,
          conversations: Math.floor(Math.random() * 30) + 5,
          users: currentStats?.total_users || 100,
          newUsers: Math.floor(Math.random() * 10) + 1,
        };
      });
      return sampleData;
    }

    if (mode === "daily") {
      // Return last 7 days data
      return dailyStats
        .slice(-7) // Get last 7 days
        .map((stat) => ({
          month: new Date(stat.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          requests: stat.requests || 0,
          conversations: stat.conversations || stat.requests || 0,
          users: currentStats?.total_users || 100,
          newUsers: Math.ceil((stat.requests || 0) / 10),
        }));
    } else if (mode === "weekly") {
      // Group by weeks
      const weeklyMap = new Map<
        string,
        {
          requests: number;
          conversations: number;
          users: number;
          newUsers: number;
        }
      >();

      dailyStats.forEach((stat) => {
        const date = new Date(stat.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Start of week
        const weekKey = `Week ${
          weekStart.getMonth() + 1
        }/${weekStart.getDate()}`;

        if (!weeklyMap.has(weekKey)) {
          weeklyMap.set(weekKey, {
            requests: 0,
            conversations: 0,
            users: 0,
            newUsers: 0,
          });
        }

        const existing = weeklyMap.get(weekKey)!;
        existing.requests += stat.requests;
        existing.conversations += stat.requests;
      });

      return Array.from(weeklyMap.entries())
        .map(([week, data]) => ({
          month: week,
          ...data,
          users: stats?.total_users || 0,
          newUsers: Math.ceil(data.requests / 100),
        }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-4); // Last 4 weeks
    } else {
      // Group by months
      const monthlyMap = new Map<
        string,
        {
          requests: number;
          conversations: number;
          users: number;
          newUsers: number;
        }
      >();

      dailyStats.forEach((stat) => {
        const date = new Date(stat.date);
        const monthKey = `${date.getFullYear()}/${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, {
            requests: 0,
            conversations: 0,
            users: 0,
            newUsers: 0,
          });
        }

        const existing = monthlyMap.get(monthKey)!;
        existing.requests += stat.requests;
        existing.conversations += stat.requests;
      });

      return Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month,
          ...data,
          users: stats?.total_users || 0,
          newUsers: Math.ceil(data.requests / 200), // Monthly new user estimate
        }))
        .sort((a, b) => a.month.localeCompare(b.month));
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadDashboardData();
      message.success(t("dashboard.messages.refreshed"));
    } catch (error: any) {
      message.error(error.message || t("dashboard.messages.refreshError"));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDateRangeChange = async (
    dates: any,
    dateStrings: [string, string]
  ) => {
    if (dates && dates.length === 2) {
      try {
        setLoading(true);
        const data = await dashboardService.getStats(
          undefined,
          dateStrings[0],
          dateStrings[1]
        );
        setStats(data);

        // Process data based on current view mode (with validation)
        const dailyStats =
          (data as any).trend_data || (data as any).daily_stats || [];
        const processedStats = processData(dailyStats, viewMode, data);
        setMonthlyData(processedStats);
      } catch (error: any) {
        message.error(error.message || t("dashboard.messages.filterError"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportConversations = async () => {
    try {
      message.loading({
        content: "Preparing conversations export...",
        key: "export-conversations",
      });

      let days, startDate, endDate;

      if (viewMode === "daily") {
        days = 7;
      } else if (viewMode === "weekly") {
        days = 28;
      } else {
        // Monthly - get last 12 months
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 12);
        startDate = start.toISOString().split("T")[0];
        endDate = end.toISOString().split("T")[0];
      }

      const blob = await dashboardService.exportConversations(
        days,
        startDate,
        endDate
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversations_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "Conversations exported successfully!",
        key: "export-conversations",
      });
    } catch (error: any) {
      message.error({
        content: error.message || "Failed to export conversations",
        key: "export-conversations",
      });
    }
  };

  const handleExportUserRequests = async () => {
    try {
      message.loading({
        content: "Preparing user requests export...",
        key: "export-requests",
      });

      let days, startDate, endDate;

      if (viewMode === "daily") {
        days = 7;
      } else if (viewMode === "weekly") {
        days = 28;
      } else {
        // Monthly - get last 12 months
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 12);
        startDate = start.toISOString().split("T")[0];
        endDate = end.toISOString().split("T")[0];
      }

      const blob = await dashboardService.exportUserRequests(
        days,
        startDate,
        endDate
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `user_requests_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "User requests exported successfully!",
        key: "export-requests",
      });
    } catch (error: any) {
      message.error({
        content: error.message || "Failed to export user requests",
        key: "export-requests",
      });
    }
  };

  const handleExportNewUsers = async () => {
    try {
      message.loading({
        content: "Preparing new users export...",
        key: "export-new-users",
      });

      let days, startDate, endDate;

      if (viewMode === "daily") {
        days = 7;
      } else if (viewMode === "weekly") {
        days = 28;
      } else {
        // Monthly - get last 12 months
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 12);
        startDate = start.toISOString().split("T")[0];
        endDate = end.toISOString().split("T")[0];
      }

      const blob = await dashboardService.exportNewUsers(
        days,
        startDate,
        endDate
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `new_users_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "New users exported successfully!",
        key: "export-new-users",
      });
    } catch (error: any) {
      message.error({
        content: error.message || "Failed to export new users",
        key: "export-new-users",
      });
    }
  };

  if (loading && !stats) {
    return <LoadingSpinner tip={t("common.loading")} />;
  }

  // Calculate totals for display - using real data only
  const totalNewUsers = stats?.total_users || 0;
  const totalConversations = stats?.total_conversations || 0;

  // Helper function to get trend icon
  const getTrendIcon = (change: number) => {
    if (change > 0) return <RiseOutlined className={styles.trendPositive} />;
    if (change < 0) return <FallOutlined className={styles.trendNegative} />;
    return null;
  };

  // Helper function to format change
  const formatChange = (change: number) => {
    const trendClass =
      change > 0
        ? styles.changePositive
        : change < 0
        ? styles.changeNegative
        : styles.changeNeutral;
    const prefix = change > 0 ? "+" : "";
    return (
      <span className={trendClass}>
        {prefix}
        {change}
      </span>
    );
  };

  return (
    <div className={`${styles.dashboardContainer} ${styles.fadeIn}`}>
      {/* Page Header */}
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
        icon={<DashboardOutlined />}
        extra={
          <Space>
            <RangePicker
              onChange={handleDateRangeChange}
              placeholder={[
                t("dashboard.placeholders.startDate"),
                t("dashboard.placeholders.endDate"),
              ]}
              className={styles.dateRangePicker}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={handleRefresh}
              type="primary"
              className={styles.refreshButton}
            >
              {t("dashboard.actions.refresh")}
            </Button>
          </Space>
        }
      />

      <Spin spinning={loading}>
        {/* Statistics Cards */}
        <Row gutter={[24, 24]} className={styles.statsSection}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className={`${styles.mainStatsCard} ${styles.conversations} ${styles.fadeIn}`}
            >
              <div className={styles.statsCardContent}>
                <div className={styles.statsCardInfo}>
                  <div className={styles.statsCardNumber}>
                    {stats?.total_conversations?.toLocaleString() || "0"}
                  </div>
                  <div className={styles.statsCardTitle}>
                    Total Conversation
                  </div>
                  {/* <div className={styles.statsCardChange}>
                    {stats?.conversations_change &&
                    stats.conversations_change > 0 ? (
                      <>
                        <RiseOutlined />
                        {Math.abs(stats.conversations_change)}% Up from
                        yesterday
                      </>
                    ) : stats?.conversations_change &&
                      stats.conversations_change < 0 ? (
                      <>
                        <FallOutlined />
                        {Math.abs(stats.conversations_change)}% Down from
                        yesterday
                      </>
                    ) : (
                      "No change from yesterday"
                    )}
                  </div> */}
                </div>
                <div className={styles.statsCardIcon}>
                  <MessageOutlined />
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className={`${styles.mainStatsCard} ${styles.users} ${styles.fadeIn}`}
            >
              <div className={styles.statsCardContent}>
                <div className={styles.statsCardInfo}>
                  <div className={styles.statsCardNumber}>
                    {stats?.total_users?.toLocaleString() || "0"}
                  </div>
                  <div className={styles.statsCardTitle}>Total User</div>
                  {/* <div className={styles.statsCardChange}>
                    {stats?.users_change && stats.users_change > 0 ? (
                      <>
                        <RiseOutlined />
                        {Math.abs(stats.users_change)}% Up from yesterday
                      </>
                    ) : stats?.users_change && stats.users_change < 0 ? (
                      <>
                        <FallOutlined />
                        {Math.abs(stats.users_change)}% Down from yesterday
                      </>
                    ) : (
                      "No change from yesterday"
                    )}
                  </div> */}
                </div>
                <div className={styles.statsCardIcon}>
                  <TeamOutlined />
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className={`${styles.mainStatsCard} ${styles.naviRequests} ${styles.fadeIn}`}
            >
              <div className={styles.statsCardContent}>
                <div className={styles.statsCardInfo}>
                  <div className={styles.statsCardNumber}>
                    {stats?.total_requests?.toLocaleString() || "0"}
                  </div>
                  <div className={styles.statsCardTitle}>
                    Total Navi Request
                  </div>
                  {/* <div className={styles.statsCardChange}>
                    {stats?.requests_change && stats.requests_change > 0 ? (
                      <>
                        <RiseOutlined />
                        {Math.abs(stats.requests_change)}% Up from yesterday
                      </>
                    ) : stats?.requests_change && stats.requests_change < 0 ? (
                      <>
                        <FallOutlined />
                        {Math.abs(stats.requests_change)}% Down from yesterday
                      </>
                    ) : (
                      "No change from yesterday"
                    )}
                  </div> */}
                </div>
                <div className={styles.statsCardIcon}>
                  <CompassOutlined />
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className={`${styles.mainStatsCard} ${styles.ticketRequests} ${styles.fadeIn}`}
            >
              <div className={styles.statsCardContent}>
                <div className={styles.statsCardInfo}>
                  <div className={styles.statsCardNumber}>
                    {stats?.total_channels?.toLocaleString() || "0"}
                  </div>
                  <div className={styles.statsCardTitle}>
                    Total Ticket Request
                  </div>
                  {/* <div className={styles.statsCardChange}>
                    {stats?.channels_change && stats.channels_change > 0 ? (
                      <>
                        <RiseOutlined />
                        {Math.abs(stats.channels_change)}% Up from yesterday
                      </>
                    ) : stats?.channels_change && stats.channels_change < 0 ? (
                      <>
                        <FallOutlined />
                        {Math.abs(stats.channels_change)}% Down from yesterday
                      </>
                    ) : (
                      "No change from yesterday"
                    )}
                  </div> */}
                </div>
                <div className={styles.statsCardIcon}>
                  <FileSearchOutlined />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
        {/* View Mode Selector */}
        <Row gutter={[24, 24]} className={styles.viewModeSection}>
          <Col span={24}>
            <Card className={styles.viewModeCard}>
              <Space size="middle">
                <Typography.Text strong>
                  {t("dashboard.viewMode.label")}
                </Typography.Text>
                <Select
                  value={viewMode}
                  onChange={(value: ViewMode) => setViewMode(value)}
                  className={styles.viewModeSelect}
                >
                  <Select.Option value="daily">
                    {t("dashboard.viewMode.daily")}
                  </Select.Option>
                  <Select.Option value="weekly">
                    {t("dashboard.viewMode.weekly")}
                  </Select.Option>
                  <Select.Option value="monthly">
                    {t("dashboard.viewMode.monthly")}
                  </Select.Option>
                </Select>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Charts Section - Redesigned Professional Layout */}
        <Row gutter={[24, 24]} className={styles.chartsSection}>
          {/* Conversations Chart - Expanded */}
          <Col xs={24} lg={16}>
            <div className={styles.chartCardContainer}>
              <Card
                className={`${styles.chartCard} ${styles.fadeIn}`}
                title={
                  <div className={styles.chartTitleWithTotal}>
                    <div className={styles.chartTitle}>
                      <MessageOutlined />
                      <span>{t("dashboard.charts.conversations")}</span>
                    </div>
                    {/* <div className={`${styles.totalBadge} ${styles.conversations}`}>
                      {t('common.total')}: {totalConversations.toLocaleString()}
                    </div> */}
                  </div>
                }
                extra={
                  <Button
                    type="primary"
                    size="middle"
                    icon={<DownloadOutlined />}
                    onClick={handleExportConversations}
                    className={`${styles.exportButton} ${styles.conversations}`}
                  >
                    Export
                  </Button>
                }
              >
                <div className={styles.chartContainer}>
                  {monthlyData.length > 0 ? (
                    <div className={styles.chartContainerLarge}>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={monthlyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12, fill: "#64748b" }}
                            axisLine={{ stroke: "#e2e8f0" }}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: "#64748b" }}
                            axisLine={{ stroke: "#e2e8f0" }}
                          />
                          <RechartsTooltip
                            contentStyle={CHART_CONFIG.tooltip}
                          />
                          <Bar
                            dataKey="conversations"
                            fill="url(#conversationsGradient)"
                            name="Conversations"
                            radius={[4, 4, 0, 0]}
                          />
                          <defs>
                            <linearGradient
                              id="conversationsGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#34d399" />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <MessageOutlined className={styles.emptyIcon} />
                      <Text type="secondary">
                        {t("dashboard.messages.noDataAvailable")}
                      </Text>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </Col>

          {/* New Users Chart - Enhanced with Export */}
          <Col xs={24} lg={8}>
            <div className={styles.chartCardContainer}>
              <Card
                className={`${styles.chartCard} ${styles.fadeIn}`}
                title={
                  <div className={styles.chartTitleWithTotal}>
                    <div className={styles.chartTitle}>
                      <UserOutlined />
                      <span>{t("dashboard.charts.newUsers")}</span>
                    </div>
                    {/* <div className={`${styles.totalBadge} ${styles.newUsers}`}>
                      {t('common.total')}: {totalNewUsers.toLocaleString()}
                    </div> */}
                  </div>
                }
                extra={
                  <Button
                    type="primary"
                    size="middle"
                    icon={<DownloadOutlined />}
                    onClick={handleExportNewUsers}
                    className={`${styles.exportButton} ${styles.newUsers}`}
                  >
                    Export
                  </Button>
                }
              >
                <div className={styles.chartContainer}>
                  {monthlyData.length > 0 ? (
                    <div className={styles.chartContainerSmall}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={monthlyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            axisLine={{ stroke: "#e2e8f0" }}
                          />
                          <YAxis
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            axisLine={{ stroke: "#e2e8f0" }}
                          />
                          <RechartsTooltip
                            contentStyle={CHART_CONFIG.tooltip}
                          />
                          <Bar
                            dataKey="newUsers"
                            fill="url(#newUsersGradient)"
                            name={t("dashboard.charts.newUsers")}
                            radius={[4, 4, 0, 0]}
                          />
                          <defs>
                            <linearGradient
                              id="newUsersGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop offset="0%" stopColor="#f97316" />
                              <stop offset="100%" stopColor="#fb923c" />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <UserOutlined className={styles.emptyIcon} />
                      <Text type="secondary">
                        {t("dashboard.messages.noDataAvailable")}
                      </Text>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Recent Activity - Full Width */}
        <Row gutter={[24, 24]} className={styles.recentActivitySection}>
          <Col xs={24}>
            <Card
              className={`${styles.chartCard} ${styles.recentActivityCard} ${styles.fadeIn}`}
              title={
                <div className={styles.cardTitle}>
                  <HistoryOutlined className={styles.chartIcon} />
                  <span>Recent Activity</span>
                </div>
              }
              extra={
                <Button
                  type="text"
                  onClick={() => (window.location.href = "/history")}
                  className={styles.viewAllButton}
                >
                  View All →
                </Button>
              }
            >
              <Row gutter={[16, 16]}>
                {SAMPLE_ACTIVITIES.map((item, index) => (
                  <Col xs={24} sm={12} lg={6} key={index}>
                    <div
                      className={`${styles.activityItemCard} ${
                        styles[item.status]
                      }`}
                    >
                      <div className={styles.activityItemHeader}>
                        <Avatar
                          size={36}
                          icon={getActivityIcon(item.iconType, item.color)}
                          className={styles.activityItemAvatar}
                        />
                        <div className={styles.activityItemInfo}>
                          <Text strong className={styles.activityItemTitle}>
                            {item.title}
                          </Text>
                          <Text
                            type="secondary"
                            className={styles.activityItemTime}
                          >
                            {item.time}
                          </Text>
                        </div>
                      </div>
                      <Text className={styles.activityItemDescription}>
                        {item.description}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Card
          className={`${styles.chartCard} ${styles.quickActionsCard} ${styles.fadeIn}`}
          title={
            <div className={styles.cardTitle}>
              <SettingOutlined className={styles.chartIcon} />
              <span>Quick Actions</span>
            </div>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6}>
              <Button
                className={`${styles.quickActionButton} ${styles.primary}`}
                size="large"
                icon={<FileTextOutlined />}
                onClick={() => (window.location.href = "/process")}
              >
                <div className={styles.quickActionTitle}>Process Documents</div>
                <div className={styles.quickActionSubtitle}>
                  Upload & Extract
                </div>
              </Button>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <Button
                className={`${styles.quickActionButton} ${styles.success}`}
                size="large"
                icon={<BlockOutlined />}
                onClick={() => (window.location.href = "/chunks")}
              >
                <div className={styles.quickActionTitle}>Manage Chunks</div>
                <div className={styles.quickActionSubtitle}>Review & Edit</div>
              </Button>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <Button
                className={`${styles.quickActionButton} ${styles.warning}`}
                size="large"
                icon={<HistoryOutlined />}
                onClick={() => (window.location.href = "/history")}
              >
                <div className={styles.quickActionTitle}>View History</div>
                <div className={styles.quickActionSubtitle}>Activity Log</div>
              </Button>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <Button
                className={`${styles.quickActionButton} ${styles.purple}`}
                size="large"
                icon={<MessageOutlined />}
                onClick={() => (window.location.href = "/chat")}
              >
                <div className={styles.quickActionTitle}>Knowledge Chat</div>
                <div className={styles.quickActionSubtitle}>AI Assistant</div>
              </Button>
            </Col>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

export default Dashboard;
